import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Heart, Gift, ArrowRight, Instagram } from "lucide-react";
import { getCategories, getProducts, getHeroImages, SHOP } from "@/lib/api";
import { CategoryChips } from "@/components/CategoryChips";
import { ProductCard } from "@/components/ProductCard";
import { SectionTitle } from "@/components/Section";
import { SmartImage } from "@/components/SmartImage";
import { imgUrl, imgSrcSet } from "@/lib/imageUrl";
import { ReviewsSection } from "@/pages/ReviewsPage";

const FALLBACK_HERO = [
  "https://images.unsplash.com/photo-1494972308805-463bc619d34e?auto=format&fit=crop&q=80&w=1200",
];

const HeroCarousel = () => {
  const [images, setImages] = useState(FALLBACK_HERO);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    getHeroImages().then(r => { if (r.images?.length) setImages(r.images); }).catch(() => {});
  }, []);
  // Preload the first (LCP) hero image so the browser fetches it as early as possible.
  useEffect(() => {
    if (!images.length) return;
    const href = imgUrl(images[0], 900, { quality: 82 });
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = href;
    link.setAttribute("fetchpriority", "high");
    const srcSet = imgSrcSet(images[0], [480, 640, 800, 1000, 1200]);
    if (srcSet) {
      link.setAttribute("imagesrcset", srcSet);
      link.setAttribute("imagesizes", "(max-width: 768px) 100vw, 50vw");
    }
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch (_) {} };
  }, [images]);
  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % images.length), 5000);
    return () => clearInterval(t);
  }, [images]);
  return (
    <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(216,27,96,0.15)]" data-testid="hero-carousel">
      {images.map((src, i) => (
        <SmartImage
          key={src + i}
          src={src}
          alt="Sugandhi crochet bouquet"
          width={1000}
          widths={[480, 640, 800, 1000, 1200]}
          sizes="(max-width: 768px) 100vw, 50vw"
          priority={i === 0}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ opacity: i === idx ? 1 : 0 }}
        />
      ))}
      <div className="absolute bottom-4 left-4 right-4 bg-white/80 backdrop-blur-lg rounded-2xl p-3 flex items-center gap-3 z-10">
        <div className="w-10 h-10 rounded-full bg-[#D81B60] text-white flex items-center justify-center"><Gift size={18} /></div>
        <div className="flex-1">
          <p className="text-xs text-[#8E6377]">Use code</p>
          <p className="font-bold text-[#D81B60] tracking-wider">SUGANDHI10</p>
        </div>
        <span className="text-sm font-serif text-[#2D0A22]">10% OFF</span>
      </div>
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10" data-testid="hero-dots">
          {images.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60"}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const CategorySection = ({ category, priority = false }) => {
  const [products, setProducts] = useState([]);
  useEffect(() => { getProducts({ category: category.id }).then(setProducts); }, [category.id]);

  // Preload the first row of product covers when this section is above the fold.
  useEffect(() => {
    if (!priority || !products.length) return;
    const links = [];
    products.slice(0, 4).forEach((p) => {
      const cover = p.images?.[0];
      if (!cover) return;
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = imgUrl(cover, 500, { quality: 80 });
      link.setAttribute("fetchpriority", "high");
      const ss = imgSrcSet(cover, [240, 320, 480, 640]);
      if (ss) {
        link.setAttribute("imagesrcset", ss);
        link.setAttribute("imagesizes", "(max-width: 768px) 50vw, 25vw");
      }
      document.head.appendChild(link);
      links.push(link);
    });
    return () => { links.forEach((l) => { try { document.head.removeChild(l); } catch (_) {} }); };
  }, [priority, products]);

  return (
    <section id={`section-${category.id}`} className="max-w-6xl mx-auto px-4 sm:px-6 py-8 scroll-mt-24" data-testid={`section-${category.id}`}>
      <SectionTitle
        eyebrow="Handcrafted Collection"
        title={category.name}
        action="View all"
        actionTo={`/shop?category=${category.id}`}
      />
      {products.length === 0 ? (
        <p className="text-[#8E6377] text-sm">Loading...</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {products.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} priority={priority && i < 4} />)}
        </div>
      )}
    </section>
  );
};

const ComingSoonBanner = ({ category }) => (
  <section id={`section-${category.id}`} className="max-w-6xl mx-auto px-4 sm:px-6 py-6 scroll-mt-24">
    <SectionTitle title={category.name} />
    <div className="rounded-3xl bg-gradient-to-br from-[#FCE4EC] to-[#F8BBD0] p-8 text-center">
      <div className="text-4xl mb-2">🌷</div>
      <p className="font-serif text-xl text-[#2D0A22]">Coming Soon</p>
      <p className="text-sm text-[#8E6377] mt-1">Our {category.name} collection is being stitched with love. Check back soon!</p>
    </div>
  </section>
);

export const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => { getCategories().then(setCategories); }, []);

  const onSelect = (id) => {
    setActiveCat(id);
    setTimeout(() => {
      const el = document.getElementById(`section-${id === "all" ? "roses" : id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const shopCats = categories.filter(c => c.id !== "all");

  return (
    <div className="pb-24 md:pb-8">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grain-bg absolute inset-0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-12 md:pt-16 md:pb-20 grid md:grid-cols-2 gap-8 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FCE4EC] rounded-full text-[#D81B60] text-xs font-semibold mb-4">
              <Sparkles size={14} /> Handcrafted in Vita, Maharashtra
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D0A22] leading-[1.05]">
              Bloom that lasts <span className="italic text-[#D81B60]">forever</span>.
            </h1>
            <p className="font-script text-2xl text-[#D81B60] mt-2">crochet flowers · made with love</p>
            <p className="mt-4 text-[#8E6377] text-base max-w-md leading-relaxed">
              Beautiful crochet bouquets, gifting boxes & flower pots — carefully handmade by Sugandhi Flowers. Fresh looks that never wilt.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#section-roses" data-testid="hero-shop-button" className="pill-btn inline-flex items-center gap-2">
                Shop Bouquets <ArrowRight size={16} />
              </a>
              <Link to="/custom" data-testid="hero-custom-button" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#D81B60] text-[#D81B60] font-semibold hover:bg-[#FCE4EC]">
                Design Your Own
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-4 text-xs text-[#8E6377]">
              <a href={SHOP.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[#D81B60]">
                <Instagram size={14} /> {SHOP.instagramHandle} · {SHOP.followers}
              </a>
              <span>·</span>
              <span>Delivering PAN India</span>
            </div>
          </div>

          <div className="relative">
            <HeroCarousel />
            <div className="absolute -top-4 -right-2 hidden md:block bg-white/90 backdrop-blur-lg rounded-2xl px-3 py-2 shadow-lg rotate-3 z-10">
              <p className="font-script text-xl text-[#D81B60]">Forever fresh</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories chips - compact sticky */}
      <section className="max-w-6xl mx-auto py-2 sticky top-[57px] md:top-[65px] z-30 bg-[#FFFBFB]/90 backdrop-blur-md border-b border-[#F4D8E2]">
        <CategoryChips categories={categories} active={activeCat} onSelect={onSelect} />
      </section>

      {/* Valentine's promo banner */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FCE4EC] via-[#F8BBD0] to-[#F48FB1] p-6 md:p-10">
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/70 backdrop-blur rounded-full text-[#AD1457] text-xs font-bold mb-3">
              <Heart size={12} fill="currentColor" /> Valentine's Collection
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-[#2D0A22] leading-tight">Say it with roses that never fade.</h3>
            <p className="mt-2 text-[#8E6377]">Curated bouquets to say "I love you" — softer, sweeter, and forever.</p>
            <a href="#section-valentine" className="mt-4 inline-flex items-center gap-2 pill-btn">
              Shop Valentine's <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* One dedicated section per category */}
      {shopCats.map((cat, i) => cat.coming_soon ? (
        <ComingSoonBanner key={cat.id} category={cat} />
      ) : (
        <CategorySection key={cat.id} category={cat} priority={i === 0} />
      ))}

      {/* Customer Reviews */}
      <ReviewsSection limit={4} />
    </div>
  );
};
